// src/pages/components/VendasMensaisVendedoresDashboard.js
import { useEffect, useMemo, useState } from "react";

import { useTheme } from "@mui/material/styles";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import { Card, CircularProgress, IconButton, Tooltip } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function monthLabel(ano, mes) {
  const labels = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];
  const mm = Number(mes) || 1;
  const aa = String(ano || "").slice(-2);
  return `${labels[mm - 1] || "?"} ${aa}`;
}

export default function VendasMensaisVendedoresDashboard({ height = "16rem", fullscreen = false }) {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  // adminLike (sem bloquear hooks)
  const isAdminLike = useMemo(() => {
    return ["r", "w", "u", "d"].some((t) => temPermissao("Menu Administração", t));
  }, [temPermissao]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]); // [{ ano, mes, refvendedor, vendedor, total }]
  const [outrosSel, setOutrosSel] = useState("OUTROS"); // "OUTROS" | refvendedor

  const colorMap = {
    primary: theme.palette.primary.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    secondary: theme.palette.secondary.main,
    dark: theme.palette.dark.main,
  };

  const colorKeys = ["primary", "info", "success", "warning", "error", "dark"]; // reduzido p/ não ficar demasiado “arco-íris”

  const carregar = async () => {
    setLoading(true);
    try {
      const [data, err] = await handleApi(
        api.post("Dashboard/SelVendasMensaisVendedores13Meses", {})
      );

      if (err) {
        showAlert(err, "error");
        setRows([]);
        return;
      }

      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  // só chama API se for adminLike (mas hooks continuam a ser chamados)
  useEffect(() => {
    if (!isAdminLike) return;
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminLike]);

  const chart = useMemo(() => {
    // labels dos 13 meses (inclui mês atual)
    const now = new Date();
    const months = [];
    for (let i = 12; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ ano: d.getFullYear(), mes: d.getMonth() + 1 });
    }

    const labels = months.map((m) => monthLabel(m.ano, m.mes));

    const idxByKey = {};
    months.forEach((m, i) => {
      idxByKey[`${m.ano}-${pad2(m.mes)}`] = i;
    });

    // agrupar por vendedor
    const byVend = new Map();
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      const ano = Number(r?.ano);
      const mes = Number(r?.mes);
      const idx = idxByKey[`${ano}-${pad2(mes)}`];
      if (idx === undefined) return;

      const ref = String(r?.refvendedor ?? "").trim();
      const nome = String(r?.vendedor ?? "").trim() || ref || "(sem vendedor)";
      const vendKey = ref || nome;

      if (!byVend.has(vendKey)) {
        byVend.set(vendKey, {
          refvendedor: ref,
          vendedor: nome,
          data: new Array(13).fill(0),
        });
      }

      const item = byVend.get(vendKey);
      const v = Number(r?.total);
      item.data[idx] = Number.isFinite(v) ? v : 0;
    });

    const TOP_N = 5;

    const vendAll = Array.from(byVend.values()).map((v) => ({
      ...v,
      total13: (v.data || []).reduce((acc, x) => acc + (Number(x) || 0), 0),
    }));

    // Top N
    const top = [...vendAll].sort((a, b) => (b.total13 || 0) - (a.total13 || 0)).slice(0, TOP_N);

    // Outros
    const topKeys = new Set(top.map((v) => String(v.refvendedor || v.vendedor)));
    const outros = vendAll.filter((v) => !topKeys.has(String(v.refvendedor || v.vendedor)));

    const outrosOptions = outros
      .slice()
      .sort((a, b) => String(a.vendedor).localeCompare(String(b.vendedor), "pt-PT"))
      .map((v) => ({
        ref: String(v.refvendedor || "").trim(),
        nome: String(v.vendedor || "").trim() || String(v.refvendedor || "").trim(),
      }))
      .filter((x) => x.ref);

    let outrosDataset = null;

    if (outros.length > 0) {
      // agregado
      const dataAgregado = new Array(13).fill(0);
      outros.forEach((v) => {
        (v.data || []).forEach((val, idx) => {
          dataAgregado[idx] += Number(val) || 0;
        });
      });

      if (outrosSel && outrosSel !== "OUTROS") {
        const chosen = outros.find(
          (v) => String(v.refvendedor).trim() === String(outrosSel).trim()
        );
        if (chosen) {
          outrosDataset = {
            label: `${chosen.vendedor} (${chosen.refvendedor})`,
            data: chosen.data,
            color: "secondary",
            tension: 0.35,
            _modoOutros: "single",
          };
        } else {
          outrosDataset = {
            label: `Outros (${outros.length})`,
            data: dataAgregado,
            color: "secondary",
            tension: 0.35,
            _modoOutros: "agg",
          };
        }
      } else {
        outrosDataset = {
          label: `Outros (${outros.length})`,
          data: dataAgregado,
          color: "secondary",
          tension: 0.35,
          _modoOutros: "agg",
        };
      }
    }

    const datasets = [];

    top.forEach((v, i) => {
      const color = colorKeys[i % colorKeys.length];
      const label = v.refvendedor ? `${v.vendedor} (${v.refvendedor})` : v.vendedor;
      datasets.push({
        label,
        data: v.data,
        color,
        tension: 0.35,
      });
    });

    if (outrosDataset) datasets.push(outrosDataset);

    const totaisMes = new Array(13).fill(0);
    datasets.forEach((ds) => {
      ds.data.forEach((val, i) => {
        totaisMes[i] += Number(val) || 0;
      });
    });

    const outrosModo = outrosDataset?._modoOutros || "none";

    return {
      labels,
      datasets,
      totaisMes,
      outrosOptions,
      outrosCount: outrosOptions.length,
      outrosModo,
    };
  }, [rows, outrosSel]);

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat("pt-PT", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  );

  // ✅ o return “gate” só acontece AQUI, depois de todos os hooks terem sido chamados
  if (!isAdminLike) return null;

  // console.log(height);

  return (
    <MDBox mb={2}>
      <Card>
        <MDBox p={fullscreen ? 1.5 : 2.5}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <MDTypography variant="h6" gutterBottom>
              Vendas mensais por vendedor (últimos 13 meses)
            </MDTypography>

            {height === "16rem" ? (
              <Tooltip title="Abrir em tamanho grande">
                <IconButton
                  color="info"
                  size="small"
                  onClick={() =>
                    window.open(
                      "/dashboard/vendas-mensais-vendedores",
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </MDBox>

          <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
            Top 5 vendedores + linha “Outros”. Na opção “Outros (agregado)”, a soma das linhas por
            mês será igual ao total mensal do gráfico “Vendas dos últimos 3 anos”.
          </MDTypography>

          {loading ? (
            <MDBox py={4} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          ) : !chart || (chart.datasets?.length || 0) === 0 ? (
            <MDTypography variant="button" color="text">
              Sem dados para apresentar.
            </MDTypography>
          ) : (
            <>
              {chart?.outrosCount > 0 && (
                <MDBox mb={1.5} display="flex" alignItems="flex-end" gap={2} flexWrap="wrap">
                  <MDBox>
                    <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                      Linha “Outros”
                    </label>
                    <select
                      value={outrosSel}
                      onChange={(e) => setOutrosSel(e.target.value)}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 6,
                        fontSize: 13,
                        border: "1px solid #ccc",
                        minWidth: 260,
                      }}
                    >
                      <option value="OUTROS">Outros (agregado)</option>
                      {chart.outrosOptions.map((o) => (
                        <option key={o.ref} value={o.ref}>
                          {o.nome} ({o.ref})
                        </option>
                      ))}
                    </select>
                  </MDBox>

                  {chart?.outrosModo === "single" && (
                    <MDTypography variant="caption" color="text">
                      Modo consulta: a soma mensal das linhas já não representa o total do mês.
                    </MDTypography>
                  )}
                </MDBox>
              )}

              <MDBox mb={1.5}>
                <DefaultLineChart
                  chart={{
                    labels: chart.labels,
                    datasets: chart.datasets,
                  }}
                  height={height}
                />
              </MDBox>

              {/* legenda */}
              <MDBox display="flex" flexWrap="wrap" gap={1.5}>
                {chart.datasets.map((ds) => (
                  <MDTypography
                    key={ds.label}
                    variant="button"
                    color="text"
                    sx={{
                      fontSize: 12,
                      display: "inline-flex",
                      alignItems: "center",
                      mr: 1.5,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        marginRight: 6,
                        backgroundColor: colorMap[ds.color] || "#999",
                        display: "inline-block",
                      }}
                    />
                    {ds.label}
                  </MDTypography>
                ))}
              </MDBox>

              {/* Totais (mantém só para validação; depois podes remover/ocultar) */}
              <MDBox mt={1.5} display="flex" flexWrap="wrap" gap={1.5}>
                {chart.labels.map((lab, i) => (
                  <MDTypography key={lab} variant="caption" color="text">
                    <strong>{lab}:</strong> {fmt.format(chart.totaisMes[i] || 0)} €
                  </MDTypography>
                ))}
              </MDBox>
            </>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
}
